import * as React from "react";
import { formatToApiDate } from "@repo/utils";
import { api } from "@/lib/api/client";
import { logger } from "@/lib/utils/logger";
import { getUserFriendlyError } from "@/lib/utils/error-messages";

export interface TrendDataPoint {
  date: string;
  receipts: number;
  dispenses: number;
  adjustments: number;
}

export interface TopItem {
  item_name: string;
  ndc: string;
  total_quantity: number;
}

export interface InventoryStatus {
  in_stock: number;
  reorder_needed: number;
  sold_out: number;
}

export interface AnalyticsData {
  trends: TrendDataPoint[];
  topDispensed: TopItem[];
  topReceived: TopItem[];
  inventoryStatus: InventoryStatus;
  totalItems: number;
}

export type DateRange = "7d" | "30d" | "90d";

interface UseAnalyticsDataReturn {
  data: AnalyticsData | null;
  isLoading: boolean;
  error: string | null;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  refetch: () => Promise<void>;
}

function getDateParams(range: DateRange): {
  startDate: string;
  endDate: string;
} {
  const endDate = new Date();
  const startDate = new Date();

  switch (range) {
    case "7d":
      startDate.setDate(endDate.getDate() - 7);
      break;
    case "30d":
      startDate.setDate(endDate.getDate() - 30);
      break;
    case "90d":
      startDate.setDate(endDate.getDate() - 90);
      break;
  }

  return {
    startDate: formatToApiDate(startDate),
    endDate: formatToApiDate(endDate),
  };
}

/**
 * Hook for fetching and managing analytics data with date range selection
 */
export function useAnalyticsData(): UseAnalyticsDataReturn {
  const [data, setData] = React.useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [dateRange, setDateRange] = React.useState<DateRange>("30d");

  const fetchData = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { startDate, endDate } = getDateParams(dateRange);

      const responseData = await api.get<
        { data?: AnalyticsData } & AnalyticsData
      >("/analytics", {
        params: { startDate, endDate },
      });
      const result: AnalyticsData = responseData.data || responseData;

      setData(result);
    } catch (err) {
      const message = getUserFriendlyError(
        err,
        "Failed to load analytics data"
      );
      setError(message);
      logger.error("Analytics data fetch error", err, {
        operation: "fetch_analytics_data",
        metadata: { dateRange },
      });
    } finally {
      setIsLoading(false);
    }
  }, [dateRange]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = React.useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    dateRange,
    setDateRange,
    refetch,
  };
}
